import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { adminPrograms as initialPrograms, type Program, categoryLabels, communityFollowers, programStats } from '@/mocks/adminPrograms';
import type { ProgramFormData } from './components/ProgramFormModal';
import { SwalAlert } from '@/hooks/useSweetAlert';
import { usePlanPermissions } from '@/hooks/usePlanPermissions';
import { useUpgradeModal } from '@/context/UpgradeModalContext';
import ProgramCard from './components/ProgramCard';
import ProgramFormModal from './components/ProgramFormModal';
import NotificationFlowModal from '@/pages/admin/notifications/components/NotificationFlowModal';
import type { NotificationFlowContext } from '@/pages/admin/notifications/components/NotificationFlowModal';

function formatK(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

export default function AdminProgramsPage() {
  const navigate = useNavigate();
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('todas');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProgram, setEditingProgram] = useState<Program | null>(null);
  const [communityTarget, setCommunityTarget] = useState<Program | null>(null);
  const [statsTarget, setStatsTarget] = useState<Program | null>(null);
  const [flowModal, setFlowModal] = useState<{ open: boolean; context: NotificationFlowContext }>({
    open: false,
    context: { type: 'programa', relatedModule: 'programs' },
  });
  const { currentPlan, isFree, storageUsed, storageTotalGB } = usePlanPermissions();
  const { openUpgradeModal } = useUpgradeModal();

  const loadPrograms = useCallback(() => {
    setLoading(true);
    setError(false);
    setTimeout(() => {
      try {
        setPrograms(initialPrograms);
        setLoading(false);
      } catch {
        setError(true);
        setLoading(false);
      }
    }, 500);
  }, []);

  useEffect(() => {
    loadPrograms();
  }, [loadPrograms]);

  const filtered = programs.filter((p) => {
    const matchSearch =
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.hostName.toLowerCase().includes(search.toLowerCase()) ||
      p.description.toLowerCase().includes(search.toLowerCase());
    const matchCat = categoryFilter === 'todas' || p.category === categoryFilter;
    return matchSearch && matchCat;
  });

  const totalFollowers = programs.reduce((sum, p) => sum + p.followers, 0);
  const totalInteractions = programs.reduce((sum, p) => sum + p.interactions, 0);
  const activeCount = programs.filter((p) => p.isActive).length;
  const allCategories = Array.from(new Set(programs.map((p) => p.category)));

  const handleCreate = () => {
    setEditingProgram(null);
    setModalOpen(true);
  };

  const handleEdit = (program: Program) => {
    setEditingProgram(program);
    setModalOpen(true);
  };

  const handleSave = async (data: ProgramFormData) => {
    if (editingProgram) {
      setPrograms((prev) =>
        prev.map((p) =>
          p.id === editingProgram.id
            ? {
                ...p,
                name: data.name.trim(),
                slug: data.name.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
                hostName: data.hostName.trim(),
                coHosts: data.coHosts.split(',').map((s) => s.trim()).filter(Boolean),
                category: data.category,
                description: data.description.trim(),
                imageUrl: data.imageUrl.trim(),
                schedule: data.schedule.trim(),
                days: data.days.split(',').map((s) => s.trim()).filter(Boolean),
              }
            : p
        )
      );
      SwalAlert.toast('Programa actualizado', 'success');
    } else {
      const newId = Math.max(0, ...programs.map((p) => p.id)) + 1;
      const newProgram: Program = {
        id: newId,
        name: data.name.trim(),
        slug: data.name.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
        hostName: data.hostName.trim(),
        hostPhotoUrl: '',
        coHosts: data.coHosts.split(',').map((s) => s.trim()).filter(Boolean),
        category: data.category,
        description: data.description.trim(),
        imageUrl: data.imageUrl.trim() || 'https://readdy.ai/api/search-image?query=Radio%20program%20studio%20with%20professional%20microphones%2C%20warm%20ambient%20lighting%2C%20modern%20broadcast%20equipment%2C%20clean%20aesthetic%2C%20cozy%20atmosphere&width=1200&height=600&seq=program-new-default&orientation=landscape',
        schedule: data.schedule.trim(),
        days: data.days.split(',').map((s) => s.trim()).filter(Boolean),
        isActive: true,
        followers: 0,
        interactions: 0,
        likes: 0,
        comments: 0,
        shares: 0,
        upcomingEpisodes: [],
        recentPosts: [],
        createdAt: new Date().toISOString(),
      };
      setPrograms((prev) => [...prev, newProgram]);
      SwalAlert.toast('Programa creado', 'success');
    }
    setModalOpen(false);
    setEditingProgram(null);
  };

  const handleShare = async (program: Program) => {
    await SwalAlert.info(
      'Compartir programa',
      `Copiá el enlace de <strong>${program.name}</strong> y compartilo en tus redes:`,
      `/program/${program.slug}`
    );
  };

  const handlePromote = async (program: Program) => {
    const result = await SwalAlert.confirm(
      'Promocionar programa',
      `¿Querés crear una campaña de promoción para <strong>${program.name}</strong>? Serás redirigido al panel de monetización donde podrás configurar audiencia, presupuesto y duración.`
    );
    if (result.isConfirmed) {
      navigate('/admin/monetization');
    }
  };

  const handleCommunity = (program: Program) => {
    setCommunityTarget(program);
  };

  const handleStats = (program: Program) => {
    setStatsTarget(program);
  };

  const handleNotifyProgram = (program: Program) => {
    setFlowModal({
      open: true,
      context: {
        type: 'programa',
        relatedModule: 'programs',
        relatedItemId: program.id,
        relatedItemName: program.name,
        prefilledTitle: `Arranca ${program.name}`,
        prefilledMessage: `${program.hostName} inicia ${program.name}. ${program.schedule}. ¡Sintonizá ahora!`,
      },
    });
  };

  return (
    <div className="min-h-screen bg-background-100">
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-mic-line text-background-50 text-xs"></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Programas</h1>
              <p className="text-foreground-400 text-[9px]">{programs.length} programa{programs.length !== 1 ? 's' : ''} · {activeCount} al aire</p>
            </div>
          </div>
          <Link
            to="/admin/dashboard"
            className="flex items-center gap-1.5 text-foreground-500 text-xs hover:text-foreground-700 transition-colors"
          >
            <i className="ri-arrow-left-line text-xs"></i>
            <span className="hidden sm:inline">Volver</span>
          </Link>
        </div>
      </header>

      <div className="flex">


        <main className="flex-1 min-w-0">
          {/* Desktop toolbar */}
          <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
            <div>
              <h2 className="text-foreground-950 font-bold text-base">Programas</h2>
              <p className="text-foreground-400 text-xs">
                {programs.length} programa{programs.length !== 1 ? 's' : ''} · {activeCount} al aire
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
                currentPlan.id === 'free'
                  ? 'bg-secondary-50 border border-secondary-200'
                  : currentPlan.id === 'pro'
                    ? 'bg-primary-50 border border-primary-200'
                    : 'bg-accent-50 border border-accent-200'
              }`}>
                <span className={`w-2 h-2 rounded-full ${
                  currentPlan.id === 'free'
                    ? 'bg-secondary-500'
                    : currentPlan.id === 'pro'
                      ? 'bg-primary-500'
                      : 'bg-accent-500'
                }`}></span>
                <span className={`text-xs font-bold ${
                  currentPlan.id === 'free'
                    ? 'text-secondary-600'
                    : currentPlan.id === 'pro'
                      ? 'text-primary-600'
                      : 'text-accent-600'
                }`}>
                  Plan {currentPlan.name}
                </span>
                <span className="text-foreground-400 text-[10px]">{storageUsed}/{storageTotalGB}GB</span>
                {isFree && (
                  <button
                    onClick={openUpgradeModal}
                    className="text-[10px] font-bold text-primary-500 hover:text-primary-600 cursor-pointer transition-colors ml-1"
                  >
                    Mejorar
                  </button>
                )}
              </div>
              <div className="w-8 h-8 rounded-full bg-primary-100 overflow-hidden shrink-0">
                <img
                  src="https://readdy.ai/api/search-image?query=Profile%20photo%20of%20male%20radio%20station%20manager%2C%20professional%20headshot%2C%20warm%20friendly%20expression%2C%20casual%20business%20attire%2C%20clean%20studio%20background%2C%20confident%20approachable%20portrait&width=80&height=80&seq=admin-user-avatar&orientation=squarish"
                  alt="Admin"
                  className="w-full h-full object-cover"
                />
              </div>
              <button
                onClick={handleCreate}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-add-line text-base"></i>
                Crear Programa
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 md:p-6 lg:p-8 pb-8">
            {/* KPIs */}
            {!loading && !error && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
                <div className="bg-background-50 rounded-xl border border-background-200/80 p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center shrink-0">
                      <i className="ri-mic-line text-primary-500 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground-950">{programs.length}</p>
                      <p className="text-[11px] text-foreground-400">Total programas</p>
                    </div>
                  </div>
                </div>
                <div className="bg-background-50 rounded-xl border border-background-200/80 p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                      <i className="ri-broadcast-line text-emerald-500 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground-950">{activeCount}</p>
                      <p className="text-[11px] text-foreground-400">Al aire</p>
                    </div>
                  </div>
                </div>
                <div className="bg-background-50 rounded-xl border border-background-200/80 p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center shrink-0">
                      <i className="ri-heart-line text-rose-500 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground-950">{formatK(totalFollowers)}</p>
                      <p className="text-[11px] text-foreground-400">Seguidores</p>
                    </div>
                  </div>
                </div>
                <div className="bg-background-50 rounded-xl border border-background-200/80 p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
                      <i className="ri-flashlight-line text-amber-500 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground-950">{formatK(totalInteractions)}</p>
                      <p className="text-[11px] text-foreground-400">Interacciones</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-5">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por nombre, locutor o descripción..."
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400/60 transition-all"
                />
              </div>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-4 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-400/60 transition-all cursor-pointer"
              >
                <option value="todas">Todas las categorías</option>
                {allCategories.map((c) => (
                  <option key={c} value={c}>{categoryLabels[c]}</option>
                ))}
              </select>
            </div>

            {/* Grid */}
            {loading ? (
              <LoadingSkeletons />
            ) : error ? (
              <ErrorState onRetry={loadPrograms} />
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                  {filtered.map((program) => (
                    <ProgramCard
                      key={program.id}
                      program={program}
                      onEdit={handleEdit}
                      onShare={handleShare}
                      onPromote={handlePromote}
                      onCommunity={handleCommunity}
                      onStats={handleStats}
                      onNotify={handleNotifyProgram}
                    />
                  ))}
                </div>

                {filtered.length === 0 && (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 mx-auto rounded-2xl bg-background-200 flex items-center justify-center mb-4">
                      <i className="ri-mic-off-line text-foreground-400 text-2xl"></i>
                    </div>
                    <p className="text-sm font-semibold text-foreground-600">Sin resultados</p>
                    <p className="text-xs text-foreground-400 mt-1">No hay programas que coincidan con los filtros.</p>
                  </div>
                )}

                {filtered.length > 0 && (
                  <p className="mt-4 text-xs text-foreground-400">
                    Mostrando {filtered.length} de {programs.length} programa{programs.length !== 1 ? 's' : ''}
                  </p>
                )}
              </>
            )}
          </div>
        </main>
      </div>



      {/* Form Modal */}
      <ProgramFormModal
        open={modalOpen}
        program={editingProgram}
        onClose={() => {
          setModalOpen(false);
          setEditingProgram(null);
        }}
        onSave={handleSave}
      />

      {/* Community Modal */}
      {communityTarget && (
        <CommunityModal
          program={communityTarget}
          onClose={() => setCommunityTarget(null)}
        />
      )}

      {/* Stats Modal */}
      {statsTarget && (
        <StatsModal
          program={statsTarget}
          onClose={() => setStatsTarget(null)}
        />
      )}
      <NotificationFlowModal open={flowModal.open} context={flowModal.context} onClose={() => setFlowModal((prev) => ({ ...prev, open: false }))} />
    </div>
  );
}

function CommunityModal({ program, onClose }: { program: Program; onClose: () => void }) {
  const followers = communityFollowers[program.id] || [];

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 pt-12 overflow-y-auto">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl w-full max-w-md overflow-hidden shadow-xl animate-fade-in-up">
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-200">
          <div>
            <h3 className="text-base font-bold text-foreground-950">Comunidad</h3>
            <p className="text-xs text-foreground-400 mt-0.5">{program.name}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200 text-foreground-400 hover:text-foreground-700 cursor-pointer transition-colors"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        <div className="px-6 py-5 max-h-[55vh] overflow-y-auto">
          {/* Stats summary */}
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="bg-background-100 rounded-xl p-3 text-center">
              <p className="text-xl font-bold text-foreground-950">{formatK(program.followers)}</p>
              <p className="text-[10px] text-foreground-400">Seguidores</p>
            </div>
            <div className="bg-background-100 rounded-xl p-3 text-center">
              <p className="text-xl font-bold text-foreground-950">{formatK(program.interactions)}</p>
              <p className="text-[10px] text-foreground-400">Interacciones</p>
            </div>
          </div>

          {/* Top followers */}
          <h4 className="text-xs font-bold text-foreground-500 uppercase tracking-wider mb-3">Seguidores destacados</h4>
          {followers.length === 0 ? (
            <p className="text-sm text-foreground-400 text-center py-6">No hay datos de comunidad todavía.</p>
          ) : (
            <div className="space-y-2.5">
              {followers.map((f, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-100/70">
                  <div className="w-10 h-10 rounded-full bg-background-200 overflow-hidden shrink-0">
                    <img src={f.avatarUrl} alt={f.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground-900">{f.name}</p>
                    <p className="text-[11px] text-foreground-400">{f.username}</p>
                  </div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${
                    f.tier === 'VIP' ? 'bg-violet-100 text-violet-700' :
                    f.tier === 'Top Fan' ? 'bg-amber-100 text-amber-700' :
                    'bg-background-200 text-foreground-500'
                  }`}>
                    {f.tier}
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className="mt-5 flex gap-2">
            <div className="flex items-center gap-1.5 flex-1 justify-center py-2.5 rounded-xl bg-background-100 text-xs text-foreground-500">
              <i className="ri-thumb-up-line"></i>
              <span>{formatK(program.likes)} likes</span>
            </div>
            <div className="flex items-center gap-1.5 flex-1 justify-center py-2.5 rounded-xl bg-background-100 text-xs text-foreground-500">
              <i className="ri-chat-3-line"></i>
              <span>{formatK(program.comments)} comentarios</span>
            </div>
            <div className="flex items-center gap-1.5 flex-1 justify-center py-2.5 rounded-xl bg-background-100 text-xs text-foreground-500">
              <i className="ri-share-forward-line"></i>
              <span>{formatK(program.shares)} compartidos</span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2.5 px-6 py-4 border-t border-background-200">
          <button
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl text-sm font-semibold text-foreground-600 bg-background-100 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

function StatsModal({ program, onClose }: { program: Program; onClose: () => void }) {
  const stats = programStats[program.id];

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 pt-12 overflow-y-auto">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl w-full max-w-md overflow-hidden shadow-xl animate-fade-in-up">
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-200">
          <div>
            <h3 className="text-base font-bold text-foreground-950">Estadísticas</h3>
            <p className="text-xs text-foreground-400 mt-0.5">{program.name}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200 text-foreground-400 hover:text-foreground-700 cursor-pointer transition-colors"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        <div className="px-6 py-5 max-h-[55vh] overflow-y-auto">
          {stats ? (
            <>
              {/* Big numbers */}
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="bg-background-100 rounded-xl p-4 text-center">
                  <p className={`text-2xl font-bold ${stats.weeklyGrowth >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                    {stats.weeklyGrowth > 0 ? '+' : ''}{stats.weeklyGrowth}%
                  </p>
                  <p className="text-[10px] text-foreground-400">Crecimiento semanal</p>
                </div>
                <div className="bg-background-100 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-foreground-950">{stats.avgListenTime}</p>
                  <p className="text-[10px] text-foreground-400">Escucha promedio</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="bg-background-100 rounded-xl p-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                    <i className="ri-time-line text-amber-500"></i>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-foreground-900">{stats.peakHour}</p>
                    <p className="text-[10px] text-foreground-400">Hora pico</p>
                  </div>
                </div>
                <div className="bg-background-100 rounded-xl p-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-sky-50 flex items-center justify-center shrink-0">
                    <i className="ri-earth-line text-sky-500"></i>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-foreground-900">{stats.topCountry}</p>
                    <p className="text-[10px] text-foreground-400">País #1</p>
                  </div>
                </div>
              </div>

              {/* Interaction breakdown */}
              <h4 className="text-xs font-bold text-foreground-500 uppercase tracking-wider mb-3">Distribución de interacciones</h4>
              <div className="space-y-2.5">
                {(() => {
                  const total = program.likes + program.comments + program.shares || 1;
                  const likesPct = Math.round((program.likes / total) * 100);
                  const commentsPct = Math.round((program.comments / total) * 100);
                  const sharesPct = 100 - likesPct - commentsPct;
                  return (
                    <>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[11px] text-foreground-600 flex items-center gap-1.5">
                            <i className="ri-thumb-up-line text-rose-500"></i> Likes
                          </span>
                          <span className="text-[11px] font-bold text-foreground-700">{likesPct}%</span>
                        </div>
                        <div className="w-full h-2 rounded-full bg-background-200 overflow-hidden">
                          <div className="h-full rounded-full bg-rose-400" style={{ width: `${likesPct}%` }}></div>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[11px] text-foreground-600 flex items-center gap-1.5">
                            <i className="ri-chat-3-line text-sky-500"></i> Comentarios
                          </span>
                          <span className="text-[11px] font-bold text-foreground-700">{commentsPct}%</span>
                        </div>
                        <div className="w-full h-2 rounded-full bg-background-200 overflow-hidden">
                          <div className="h-full rounded-full bg-sky-400" style={{ width: `${commentsPct}%` }}></div>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[11px] text-foreground-600 flex items-center gap-1.5">
                            <i className="ri-share-forward-line text-emerald-500"></i> Compartidos
                          </span>
                          <span className="text-[11px] font-bold text-foreground-700">{sharesPct}%</span>
                        </div>
                        <div className="w-full h-2 rounded-full bg-background-200 overflow-hidden">
                          <div className="h-full rounded-full bg-emerald-400" style={{ width: `${sharesPct}%` }}></div>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </>
          ) : (
            <p className="text-sm text-foreground-400 text-center py-10">No hay estadísticas disponibles para este programa.</p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2.5 px-6 py-4 border-t border-background-200">
          <button
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl text-sm font-semibold text-foreground-600 bg-background-100 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

function LoadingSkeletons() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="bg-background-50 rounded-2xl border border-background-200/80 overflow-hidden">
          <div className="w-full aspect-[16/10] bg-background-200 animate-pulse"></div>
          <div className="p-4 space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-background-200 animate-pulse shrink-0"></div>
              <div className="space-y-1.5 flex-1">
                <div className="h-3.5 w-24 bg-background-200 rounded animate-pulse"></div>
                <div className="h-2.5 w-32 bg-background-200 rounded animate-pulse"></div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="h-14 bg-background-200 rounded-xl animate-pulse"></div>
              <div className="h-14 bg-background-200 rounded-xl animate-pulse"></div>
            </div>
            <div className="flex gap-2 pt-1">
              <div className="h-9 flex-1 bg-background-200 rounded-xl animate-pulse"></div>
              <div className="h-9 flex-1 bg-background-200 rounded-xl animate-pulse"></div>
              <div className="h-9 flex-1 bg-background-200 rounded-xl animate-pulse"></div>
              <div className="h-9 w-9 bg-background-200 rounded-xl animate-pulse"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="text-center py-16">
      <div className="w-16 h-16 mx-auto rounded-2xl bg-red-100 flex items-center justify-center mb-4">
        <i className="ri-error-warning-line text-red-500 text-2xl"></i>
      </div>
      <h3 className="text-base font-bold text-foreground-900 mb-1">Error al cargar programas</h3>
      <p className="text-sm text-foreground-500 mb-6 max-w-md mx-auto">
        Ocurrió un problema al intentar cargar la lista de programas. Revisá tu conexión y volvé a intentarlo.
      </p>
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap"
      >
        <i className="ri-refresh-line"></i>
        Reintentar
      </button>
    </div>
  );
}